import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { SupportCards } from '../components/SupportCards';

interface FaqCategory {
  name: string;
  items: Array<{ id: string; question: string; answer: string }>;
}

/** Anchor id for a topic, so the topic menu can jump to it. */
const anchor = (name: string) => `faq-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

/**
 * The help centre: published FAQs, grouped as Site Content groups them.
 *
 * A centred hero with search, then a two-column body — a sticky topic menu
 * that tracks the section in view, and one card per topic. Answers are plain
 * text, so they render as text; each question is a native disclosure, keyboard
 * and screen-reader friendly without script of its own.
 */
export function Faq() {
  const [categories, setCategories] = useState<FaqCategory[] | null>(null);
  const [term, setTerm] = useState('');
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Help centre — SkillXzen';
    api<{ categories: FaqCategory[] }>('/site/faqs')
      .then((result) => setCategories(result.categories))
      .catch(() => setCategories([]));
  }, []);

  const query = term.trim().toLowerCase();
  const shown = useMemo(() => {
    if (!categories) return null;
    if (!query) return categories;
    return categories
      .map((category) => ({
        ...category,
        items: category.items.filter((item) =>
          `${item.question} ${item.answer}`.toLowerCase().includes(query),
        ),
      }))
      .filter((category) => category.items.length > 0);
  }, [categories, query]);

  const total = shown?.reduce((sum, category) => sum + category.items.length, 0) ?? 0;
  const allCount = categories?.reduce((sum, category) => sum + category.items.length, 0) ?? 0;

  // Highlight the topic whose card is nearest the top of the screen.
  useEffect(() => {
    if (!shown || shown.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setCurrent(visible[0].target.id);
      },
      { rootMargin: '-90px 0px -55% 0px' },
    );
    shown.forEach((category) => {
      const el = document.getElementById(anchor(category.name));
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [shown]);

  return (
    <>
      <section className="cat__hero helphero">
        <div className="wrap helphero__inner">
          <p className="helphero__eyebrow">Help centre</p>
          <h1 className="helphero__title">How can we help?</h1>
          <p className="helphero__lede">
            Answers to what learners and organizations ask most about courses, certificates, billing
            and team accounts.
          </p>
          <form className="helphero__search" role="search" onSubmit={(e) => e.preventDefault()}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="m14 14 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              placeholder="Search for answers"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              aria-label="Search the help centre"
            />
            {term ? (
              <button type="button" onClick={() => setTerm('')} aria-label="Clear search">
                ×
              </button>
            ) : null}
          </form>
          {categories ? (
            <p className="helphero__meta">
              {allCount} answers across {categories.length} topics
            </p>
          ) : null}
        </div>
      </section>

      <div className="wrap">
        <div className="helpbody">
          <aside className="helpnav" aria-label="Topics">
            <p className="helpnav__label">Topics</p>
            {(categories ?? []).map((category) => {
              const id = anchor(category.name);
              const hidden = query && !shown?.some((c) => c.name === category.name);
              return (
                <a
                  key={category.name}
                  href={`#${id}`}
                  className={`helpnav__link${current === id ? ' is-current' : ''}${hidden ? ' is-dim' : ''}`}
                  aria-current={current === id ? 'true' : undefined}
                >
                  <span>{category.name}</span>
                  <span className="helpnav__count">{category.items.length}</span>
                </a>
              );
            })}
          </aside>

          <div className="helpmain">
            {!shown ? (
              <div className="skel" style={{ height: 360 }} />
            ) : (
              <>
                {query ? (
                  <p className="helpmain__result" aria-live="polite">
                    {total === 0
                      ? `No answers match “${term.trim()}”. Try a different word, or contact us below.`
                      : `${total} answer${total === 1 ? '' : 's'} for “${term.trim()}”`}
                  </p>
                ) : null}

                {shown.length === 0 && !query ? (
                  <div className="empty">No questions have been published yet.</div>
                ) : null}

                {shown.map((category) => (
                  <section className="helpsection" key={category.name} id={anchor(category.name)}>
                    <header className="helpsection__head">
                      <h2>{category.name}</h2>
                      <span>
                        {category.items.length} question{category.items.length === 1 ? '' : 's'}
                      </span>
                    </header>
                    {category.items.map((item) => (
                      // Searching opens the matches, so the answer is in view.
                      <details className="helpq" key={item.id} open={Boolean(query)}>
                        <summary>
                          <span>{item.question}</span>
                          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                            <path
                              d="m6 8 4 4 4-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </summary>
                        <p>{item.answer}</p>
                      </details>
                    ))}
                  </section>
                ))}
              </>
            )}

            <SupportCards current="/faq" />
          </div>
        </div>
      </div>
    </>
  );
}
